import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const input = this.refs.quantityInput;
    if (!input) return;

    let current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;

    // solis = 1 (ignorējam HTML step)
    let next = current + 1;

    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? parseInt(maxAttr, 10) : null;

    if (max !== null && !isNaN(max) && next > max) {
      next = max;
    }

    input.value = String(next);
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const input = this.refs.quantityInput;
    if (!input) return;

    let current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;

    // solis = 1 (ignorējam HTML step)
    let next = current - 1;

    const minAttr = input.getAttribute('min');
    const min = minAttr ? parseInt(minAttr, 10) : 1;

    if (!isNaN(min) && next < min) {
      next = min;
    }

    input.value = String(next);
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value, 10);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        isNaN(newValue) ? 0 : newValue,
        Number(quantityInput.dataset.cartLine)
      )
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;

    const minAttr = quantityInput.getAttribute('min');
    const maxAttr = quantityInput.getAttribute('max');

    const min = minAttr ? parseInt(minAttr, 10) : null;
    const max = maxAttr ? parseInt(maxAttr, 10) : null;

    let value = parseInt(quantityInput.value, 10);
    if (isNaN(value)) value = min ?? 1;

    if (min !== null && !isNaN(min) && value < min) {
      value = min;
    }
    if (max !== null && !isNaN(max) && value > max) {
      value = max;
    }

    quantityInput.value = String(value);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
